import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '@/hooks/useAppStore';
import { ImageConverter } from '@/lib/imageConverter';
import { formatFileSize, OUTPUT_FORMATS } from '@/types';
import type { ConversionOptions } from '@/types';
import { useState, useCallback } from 'react';

export function FileList() {
  const { files, removeFile, updateFile } = useAppStore();
  const [convertingIds, setConvertingIds] = useState<Set<string>>(new Set());

  const handleConvert = useCallback(async (id: string) => {
    const file = files.find((f) => f.id === id);
    if (!file || file.status !== 'pending') return;

    setConvertingIds((prev) => new Set(prev).add(id));

    try {
      const converter = ImageConverter.getInstance();
      const options: ConversionOptions = {
        format: file.targetFormat,
        quality: file.quality,
        keepAspectRatio: true,
      };

      const result = await converter.convert(file.file, options, (progress) => {
        useAppStore.getState().updateFileStatus(id, 'converting', progress);
      });

      useAppStore.getState().setFileResult(id, result);
    } catch (error) {
      useAppStore.getState().setFileError(
        id,
        error instanceof Error ? error.message : '转换失败'
      );
    } finally {
      setConvertingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [files]);

  const handleDownload = useCallback((id: string) => {
    const file = files.find((f) => f.id === id);
    if (!file?.result) return;

    const link = document.createElement('a');
    link.href = file.result.url;
    link.download = file.result.name;
    link.click();
  }, [files]);

  if (files.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 space-y-4">
      <AnimatePresence>
        {files.map((file, index) => (
          <motion.div
            key={file.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: index * 0.05 }}
            className="glass rounded-xl p-4 card-hover"
          >
            <div className="flex items-center gap-4">
              {/* Preview */}
              <div className="w-16 h-16 rounded-lg bg-surface flex-shrink-0 overflow-hidden">
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-text-primary truncate">
                  {file.name}
                </h3>
                <p className="text-xs text-text-secondary mt-0.5">
                  {formatFileSize(file.size)}
                </p>
                
                {/* Progress bar */}
                {file.status === 'converting' && (
                  <div className="mt-2">
                    <div className="h-1 bg-surface rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary to-primary-light"
                        initial={{ width: 0 }}
                        animate={{ width: `${file.progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <p className="text-xs text-text-muted mt-1">{file.progress}%</p>
                  </div>
                )}

                {/* Result info */}
                {file.status === 'completed' && file.result && (
                  <div className="mt-1 text-xs">
                    <span className="text-success">
                      {formatFileSize(file.result.size)}
                    </span>
                    <span className="text-text-muted mx-1">→</span>
                    <span className={file.result.compressionRatio > 0 ? 'text-success' : 'text-accent'}>
                      {file.result.compressionRatio > 0 ? '-' : '+'}
                      {Math.abs(file.result.compressionRatio).toFixed(1)}%
                    </span>
                  </div>
                )}

                {/* Error */}
                {file.status === 'error' && (
                  <p className="text-xs text-accent mt-1">{file.error}</p>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-3">
                {/* Format selector */}
                {file.status === 'pending' && (
                  <select
                    value={file.targetFormat}
                    onChange={(e) =>
                      updateFile(file.id, { targetFormat: e.target.value as any })
                    }
                    className="px-2 py-1 bg-surface border border-border rounded text-xs text-text-primary focus:outline-none focus:border-primary"
                  >
                    {OUTPUT_FORMATS.map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </select>
                )}

                {/* Quality slider */}
                {file.status === 'pending' && (
                  <div className="flex items-center gap-1">
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={file.quality}
                      onChange={(e) =>
                        updateFile(file.id, { quality: Number(e.target.value) })
                      }
                      className="w-16 accent-primary"
                    />
                    <span className="text-xs text-text-secondary w-6">{file.quality}</span>
                  </div>
                )}

                {/* Actions */}
                {file.status === 'pending' && (
                  <button
                    onClick={() => handleConvert(file.id)}
                    disabled={convertingIds.has(file.id)}
                    className="px-3 py-1 bg-primary hover:bg-primary-dark text-background text-xs font-medium rounded transition-all disabled:opacity-50"
                  >
                    转换
                  </button>
                )}

                {file.status === 'completed' && (
                  <button
                    onClick={() => handleDownload(file.id)}
                    className="px-3 py-1 bg-surface-highlight hover:bg-surface-hover border border-border text-text-primary text-xs font-medium rounded transition-all"
                  >
                    下载
                  </button>
                )}

                {/* Remove button */}
                <button
                  onClick={() => removeFile(file.id)}
                  className="p-1 text-text-muted hover:text-accent transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}